import type { AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import type PaginatedResponse from '@/types/paginatedResponse';
import type {
  AssignUserMissionPayload,
  MissionCatalogItem,
  UserMission,
  UserMissionState,
  UserMissionStats
} from '@/types/userMission';
import ajax from '@/utils/ajax';

const basePath = '/api/dugout/user_missions';

export const userMissionRepository = {
  async list({
    host,
    personId,
    state,
    page = 1
  }: {
    host: string;
    personId: string;
    state: UserMissionState;
    page?: number;
  }): Promise<PaginatedResponse<UserMission[]>> {
    const response = await ajax.get({
      url: `${host}${basePath}/`,
      params: { person: personId, state, page }
    });
    return response.data;
  },

  async catalog({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<MissionCatalogItem[]> {
    const response = await ajax.get({
      url: `${host}${basePath}/catalog/`,
      params: { person: personId }
    });
    return response.data;
  },

  async stats({ host, personId }: { host: string; personId: string }): Promise<UserMissionStats> {
    const response = await ajax.get({
      url: `${host}${basePath}/stats/`,
      params: { person: personId }
    });
    return response.data;
  },

  async assign({
    host,
    data
  }: {
    host: string;
    data: AssignUserMissionPayload;
  }): Promise<UserMission> {
    const response = await ajax.post<AssignUserMissionPayload, AxiosResponse<UserMission>>({
      url: `${host}${basePath}/`,
      data
    });
    return response.data;
  }
};

const UserMissionRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async list(params: { personId: string; state: UserMissionState; page?: number }) {
      return userMissionRepository.list({ host, ...params });
    },

    async catalog(personId: string) {
      return userMissionRepository.catalog({ host, personId });
    },

    async stats(personId: string) {
      return userMissionRepository.stats({ host, personId });
    },

    async assign(data: AssignUserMissionPayload) {
      return userMissionRepository.assign({ host, data });
    }
  };
};

export default UserMissionRepository;
